<template>
    <div class="cc-tiers">
        <div class="cc-tiers__chart">
            <div class="cc-tiers__column cc-tiers__axis">
                <div class="cc-tiers__plot cc-tiers__axis-plot">
                    <div class="cc-tiers__axis-title"># Recommendations</div>
                </div>
                <div class="cc-tiers__label">&nbsp;</div>
            </div>

            <div v-for="tier in columns" :key="tier.code" class="cc-tiers__column" :class="{ 'cc-tiers__column--highlight': tier.highlight }">
                <div class="cc-tiers__plot">
                    <div class="cc-tiers__bar" :style="`height: ${tier.heightPercent}%;`">
                        <div class="cc-tiers__count">{{ tier.count }}</div>
                    </div>
                </div>
                <div class="cc-tiers__label">{{ tier.label }}</div>
            </div>
        </div>

        <div class="cc-tiers__rates">
            <div class="cc-tiers__rates-title">Engagement rate by tier</div>
            <table class="cc-tiers__rates-table">
                <thead>
                    <tr>
                        <th class="text-left">Tier</th>
                        <th v-for="tier in columns" :key="`tier-${tier.code}`" class="text-center">{{ tier.label }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td class="text-left cc-tiers__rates-label">Engagement Rate</td>
                        <td v-for="tier in columns" :key="`rate-${tier.code}`" class="text-center">{{ tier.engagementRate }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
// Every tier is shown, even at zero, so the chart keeps the same columns whatever the filters.
const TIERS = [
    { code: "T1", label: "T1" },
    { code: "T2", label: "T2" },
    { code: "T3", label: "T3" },
    { code: "T4", label: "T4" },
    { code: "NON_TARGET", label: "Non-Target", highlight: true },
];

export default {
    name: "CommandCenterTierPerformance",

    props: {
        // [{ hcp_tier, suggestions, rep_engaged, engagement_rate }], one per tier with data.
        tiers: {
            type: Array,
            required: true,
        },
    },

    computed: {
        columns() {
            const byCode = Object.fromEntries(this.tiers.map((tier) => [tier.hcp_tier, tier]));
            const unlisted = this.tiers
                .filter((tier) => !TIERS.some((known) => known.code === tier.hcp_tier))
                .map((tier) => ({ code: tier.hcp_tier, label: tier.hcp_tier }));
            const max = Math.max(0, ...this.tiers.map((tier) => Number(tier.suggestions) || 0));

            return [...TIERS, ...unlisted].map((tier) => {
                const data = byCode[tier.code];
                const count = Number(data?.suggestions) || 0;
                return {
                    ...tier,
                    count,
                    heightPercent: max > 0 ? (count / max) * 100 : 0,
                    engagementRate: data?.engagement_rate === null || data?.engagement_rate === undefined ? "—" : `${Math.round(data.engagement_rate * 100)}%`,
                };
            });
        },
    },
};
</script>

<style scoped>
.cc-tiers {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.cc-tiers__chart {
    display: flex;
    justify-content: space-around;
    align-items: flex-end;
}

.cc-tiers__column {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    min-width: 0;
    font-size: 11px;
}

/* The top padding reserves room for the count label, so bar percentages resolve against bar space only. */
.cc-tiers__plot {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    box-sizing: border-box;
    width: 100%;
    height: 110px;
    padding-top: 20px;
}

.cc-tiers__count {
    position: absolute;
    bottom: 100%;
    left: 50%;
    margin-bottom: 4px;
    transform: translateX(-50%);
    color: #262626;
    font-weight: 700;
    white-space: nowrap;
}

.cc-tiers__bar {
    position: relative;
    width: 70%;
    max-width: 48px;
    background: #404040;
    transition: height 0.3s ease-in-out;
}

.cc-tiers__label {
    margin-top: 4px;
    color: #262626;
}

/* The axis column repeats the plot and label rows so its caption stays centred on the bar area. */
.cc-tiers__axis {
    flex: none;
    padding-right: 4px;
}

.cc-tiers__axis-plot {
    align-items: center;
}

.cc-tiers__axis-title {
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    color: #5f5f5f;
    font-weight: 600;
    white-space: nowrap;
}

.cc-tiers__column--highlight .cc-tiers__bar {
    background: #c8c8c8;
}

.cc-tiers__rates {
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid #dedede;
    font-size: 11px;
}

.cc-tiers__rates-title {
    margin-bottom: 4px;
    color: #5f5f5f;
}

.cc-tiers__rates-table {
    width: 100%;
    border-collapse: collapse;
}

.cc-tiers__rates-table th {
    padding: 4px 0;
    border-bottom: 1px solid #dedede;
    color: #5f5f5f;
    font-weight: 600;
}

.cc-tiers__rates-table td {
    padding: 5px 0;
    color: #262626;
    font-weight: 700;
}

.cc-tiers__rates-table th:not(:last-child),
.cc-tiers__rates-table td:not(:last-child) {
    padding-right: 12px;
}

.cc-tiers__rates-table .cc-tiers__rates-label {
    color: #5f5f5f;
    font-weight: 600;
    white-space: nowrap;
}
</style>
